<template>
    <div
        class="sui-scroll"
        :style="styleObj"
        @touchstart="touchstartEvent"
        @touchmove.passive="touchmoveEvent"
        @touchend="touchendEvent"
        @scroll.passive="scrollEvent">
        <div
            class="sui--container"
            ref="container"
            :class="[containerClass, { 'sui--horizontal': horizontal }]"
            :style="containerStyle">
            <div
                class="sui--pull-down"
                v-if="!horizontal && this.pullDown"
                :style="promptStyle">
                <sui-icon
                    name="arrow-down"
                    :style="iconStyle"></sui-icon>{{ this.offsetY > this.threshold * 1.6 ? '松开' : '下拉' }}刷新
            </div>
            <slot></slot>
        </div>
        <div
            class="sui--msdk"
            v-show="touchStart"></div>
    </div>
</template>

<script>
import Mixin from '../../scripts/mixin'
import SuiIcon from '../icon/icon'

export default {
    name: 'sui-scroll',

    mixins: [Mixin],

    components: {
        SuiIcon
    },

    props: {

        // 是否为水平滚动
        horizontal: {
            type: Boolean,
            default: false
        },

        // 是否开启下拉刷新
        pullDown: {
            type: Boolean,
            default: false
        },

        // 触发滚动到底部事件的距离
        onReachEndDistance: {
            type: Number,
            default: 200
        },

        // 内部容器类名
        containerClass: {
            type: String,
        },

        // 内部容器样式
        containerStyle: {
            type: [Object, String],
        },

        // 自定义主题色
        colors: {
            type: Object,
        }
    },

    data() {
        return {

            // 当正在执行 touchstart 事件处理函数时锁定，避免因滑动过快导致未执行完即触发 touchomve 事件，导致触发全局滚动回弹
            lockTouchStart: false,

            touchStart: false, // 是否触发 touchStart 事件，MSDK BUG
            reachStart: false, // 是否滚动到起始端
            reachEnd: false, // 是否滚动到结束端
            originX: 0, // 触摸起始位置 X 坐标
            originY: 0, // 触摸起始位置 Y 坐标
            offset: 1, // 滚动判断非 px 单位容错量

            offsetY: 0, // 下拉状态的偏移位置
            threshold: 40, // 触发下拉刷新的偏移位置的阈值
            pullingDown: false, // 是否处于下拉状态

            isIOS: /Mac OS X/.test(navigator.userAgent),

            // 暂存上一次滚动位置，用于在 keep-alive 切换时恢复
            position: 0,
        }
    },

    computed: {
        styleObj() {
            let obj = {
                'overflow-x': this.horizontal ? 'auto' : 'hidden',
                'overflow-y': !this.horizontal ? 'auto' : 'hidden',
            }
            return obj
        },

        promptStyle() {
            return {
                'height': `${ this.threshold }px`,
                'color': this.mColor(3),
                'opacity': this.offsetY / this.threshold / 1.6
            }
        },

        iconStyle() {
            return {
                'fill': this.mColor(3),
                'transform': `rotate(${ this.offsetY > this.threshold * 1.6 ? 180 : 0 }deg)`
            }
        }
    },

    activated() {
        if (this.horizontal) {
            this.$el.scrollLeft = this.position
        } else {
            this.$el.scrollTop = this.position
        }
    },

    methods: {

        /**
         * touchstart 事件
         * @param {Object} event - 事件对象
         */
        touchstartEvent(event) {
            this.touchStart = true
            this.lockTouchStart = true
            const touch = event.targetTouches[0]
            this.originX = touch.clientX
            this.originY = touch.clientY
            
            // #region 竖向滚动
            if (this.horizontal) {
                this.lockTouchStart = false
            } else {
                
                // #region 判断是否在起始端
                if (this.$el.scrollTop < this.offset) {
                    this.offsetY = 0
                    this.pullingDown = false
                    this.$refs.container.style.transition = ''
                    if (this.isIOS && !this.pullDown) {
                        this.$el.scrollTop = this.offset
                    }
                    this.reachStart = true
                    requestAnimationFrame(() => {
                        this.lockTouchStart = false
                    })
                } else {
                    this.reachStart = false
                }
                // #endregion
                
                // #region 判断是否在结束端
                if (this.$el.scrollHeight - this.$el.offsetHeight - this.$el.scrollTop < this.offset) {
                    if (this.isIOS) {
                        this.$el.scrollTop -= this.offset
                    }
                    this.reachEnd = true
                    requestAnimationFrame(() => {
                        this.lockTouchStart = false
                    })
                } else {
                    this.reachEnd = false
                }
                // #endregion

                if (!this.reachStart && !this.reachEnd) {
                    this.lockTouchStart = false
                }
            }
            // #endregion

        },

        /**
         * touchmove 事件
         * @param {Object} event - 事件对象
         */
        touchmoveEvent(event) {
            this.touchStart = false

            if (this.lockTouchStart) {
                return
            }

            const touch = event.targetTouches[0]
            const offsetX = touch.clientX - this.originX
            this.offsetY = touch.clientY - this.originY
            if (this.horizontal) {

                // #region 阻止滚动：在横向滚动区域进行竖向滚动
                if (this.offsetY != 0 && Math.abs(offsetX / this.offsetY) < 1) {
                    return
                }
                // #endregion

            } else {

                if (this.pullDown) {
                    if (this.reachStart && this.offsetY > 0) {
                        this.pullingDown = true
                        requestAnimationFrame(() => {
                            this.$refs.container.style.transform = `translate3d(0, ${ Number.parseInt(this.offsetY / 2) }px, 0)`
                        })
                    }
                    if (this.reachStart && this.pullingDown) {
                        return
                    }
                } else {

                    // #region 阻止滚动：内容不够
                    if (this.$el.scrollHeight - this.$el.offsetHeight < this.offset) {
                        return
                    }
                    // #endregion

                    // #region 阻止滚动：非 IOS
                    if (!this.isIOS) {
                        if (this.reachStart && this.offsetY > 0 || this.reachEnd && this.offsetY < 0) {
                            return
                        }
                    }
                    // #endregion

                }
            }

            // 阻止事件冒泡以触发局部滚动
            event.stopPropagation()
        },

        touchendEvent() {
            if (this.reachStart && this.pullDown && this.offsetY > 0) {
                requestAnimationFrame(() => {
                    this.$refs.container.style.transition = `transform 500ms`
                    this.$refs.container.style.transform = `translate3d(0, 0, 0)`
                })
                if (this.offsetY > this.threshold * 1.6) {
                    this.$emit('refresh')
                }
            }
        },

        /**
         * scroll 事件
         * @param {Object} event - 事件对象
         */
        scrollEvent(event) {
            if (this.horizontal) {
                this.position = this.$el.scrollLeft
                this.$emit('scroll', this.$el.scrollLeft, event)
                if (this.$el.scrollWidth - this.$el.offsetWidth - this.$el.scrollLeft < this.onReachEndDistance + this.offset) {
                    this.$emit('reachEnd')
                }
            } else {
                this.position = this.$el.scrollTop
                this.$emit('scroll', this.$el.scrollTop, event)
                if (this.$el.scrollHeight - this.$el.offsetHeight - this.$el.scrollTop < this.onReachEndDistance + this.offset) {
                    this.$emit('reachEnd')
                }
            }
        }
    }
}
</script>

<style lang="postcss">
.sui-scroll {
    -webkit-overflow-scrolling: touch;
    > .sui--container {
        position: relative;
        min-height: 100%;
        .sui--pull-down {
            position: absolute;
            top: 0;
            left: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            font-size: 12px;
            transform: translate3d(0, -100%, 0);
            .sui-icon {
                width: 14px;
                height: 14px;
                margin-right: 4px;
                transition: transform 300ms;
            }
        }
    }
    > .sui--container.sui--horizontal {
        display: flex;
        width: max-content;
        height: 100%;
    }

    /* #region MSDK BUG：必须要进行页面重绘，scrollTop 赋值才会有效果 */
    > .sui--msdk {
        position: fixed;
        top: 0;
        left: 0;
        width: 1px;
        height: 1px;
    }
    /* #endregion */
    
}
</style>